import {
  Attributes,
  createContext,
  ForwardedRef,
  forwardRef,
  startTransition,
  use,
  useImperativeHandle,
  useState,
} from 'react'
import { useSession } from '@/context/SessionProvider'
import { getEthereumAddress, getInjectiveAddress, MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { fetchChainAccountByAddress, fetchUserByAddress, GET_USER_PROFILE } from '@/api/query'
import { Login } from './templates/Login'
import { RegistrationForm } from './templates/Registration'
import { requestFaucetFunds } from '@/api/api'
import { UserProfile } from '@/api/UserProfileContract.types'
import { ExpiryModalTemplate } from './templates/Expiry'
import { useAppToast } from '@/hooks/useAppToast'
import { delay } from '@/helpers/delay'
import { tryCatchAsync } from '@/helpers/trycatch'
import { useApolloClient } from '@apollo/client'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Text,
  Box,
  DrawerOverlay,
  Drawer,
  DrawerBody,
  DrawerContent,
  useMediaQuery,
} from '@chakra-ui/react'
import { Image } from '@/components/HOC/Image'
import { RegistrationMethods } from './templates/RegistrationMethods'
import { WalletsView } from './templates/WalletsView'
import { EmailFlow } from './templates/EmailFlow'
import React from 'react'
import { StatusView } from './templates/StatusView'
import { useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { LocalStorageKey } from '@/constants/localstorage'
import { USER_PROFILE_CONTRACT } from '@/constants/constants'
import { UserProfileContractExecuteMsgBuilder } from '@/api/UserProfileContract.msg-builder'
import { useTRPCClient } from '@/api/trpc/context'
import { EnteringMode, useEnteringModeValue } from '@/hooks/useEnteringModeValue'

export const ScreenType = {
  SignIn: {
    name: 'SignIn',
    component: RegistrationMethods,
  },
  StatusView: {
    name: 'StatusView',
    component: StatusView,
  },
  Confirm: {
    name: 'Confirm',
    component: Login,
  },
  Registration: {
    name: 'Registration',
    component: RegistrationForm,
  },
  Expiry: {
    name: 'Expiry',
    component: ExpiryModalTemplate,
  },
  Wallets: {
    name: 'Wallets',
    component: WalletsView,
  },
  EmailFlow: {
    name: 'EmailFlow',
    component: EmailFlow,
  },
}

interface User {
  address: string
  profile?: UserProfile
  referer?: string | null
}

export interface SignModalHandle {
  openSignInModal: (
    screen: keyof typeof ScreenType,
    props?: (Attributes & Parameters<(typeof ScreenType)[typeof screen]['component']>[0]) | null
  ) => void
}

export const SigninHostModal = forwardRef((_props, ref: ForwardedRef<SignModalHandle | undefined>) => {
  const { createSession, sendTransaction } = useSession()
  const { onTransactionError } = useAppToast()
  const [step, setStep] = useState<{
    screen: keyof typeof ScreenType
    props: any
    component: React.ReactElement
  } | null>(null)
  const [user, setUser] = useState<User | undefined>()
  const [address, setAddress] = useState<string | undefined>()
  const [wallet, setWallet] = useState<string | undefined>()
  const queryClient = useQueryClient()
  const apolloClient = useApolloClient()

  const navigateTo = (
    screen: keyof typeof ScreenType | null,
    props?: (Attributes & Parameters<(typeof ScreenType)[NonNullable<typeof screen>]['component']>[0]) | null
  ) => {
    if (!screen) {
      startTransition(() => {
        setStep(null)
      })
      return
    }
    let component = ScreenType[screen as keyof typeof ScreenType]?.component

    const element = React.createElement(component as any, {
      key: screen,
      onClose: () => navigateTo(null),
      ...props,
    })

    startTransition(() => {
      setStep({
        screen: screen,
        props: props,
        component: element,
      })
    })
  }

  useImperativeHandle(
    ref,
    () => ({
      openSignInModal: navigateTo,
    }),
    [ref]
  )

  const handleWalletFlow = async (walletId: string, ethAddress: string) => {
    posthog.capture('click_wallet', { wallet: walletId })
    localStorage.setItem(LocalStorageKey.LastUsedWallet, walletId)

    let address = getInjectiveAddress(ethAddress)
    setAddress(address)
    setWallet(walletId)
    const user = await fetchUserByAddress(address)

    setUser({
      address: address || '',
      profile: user?.profile,
      referer: user?.referrer,
    })

    if (user == null) {
      requestFaucetFunds(address!)
      navigateTo('Registration')
      return
    }

    navigateTo('Confirm')
  }

  const handleRegister = async (profile: UserProfile, referral: string, campaign?: string) => {
    let account = await fetchChainAccountByAddress(address)
    if (!account) {
      await requestFaucetFunds(address!)
      while (!account) {
        account = await fetchChainAccountByAddress(address)
        await delay(1000)
      }
    }

    const createSessionResult = await createSession(wallet!, getEthereumAddress(address!))
    if (createSessionResult.error) {
      onTransactionError(createSessionResult.error as any)
      throw Error()
    }

    while (true) {
      const txProfile = await tryCatchAsync(
        sendTransaction(
          [
            MsgExecuteContractCompat.fromJSON({
              sender: address!,
              contractAddress: USER_PROFILE_CONTRACT,
              msg: UserProfileContractExecuteMsgBuilder.createUser({
                profile: profile,
                referrer: referral,
              }),
            }),
          ],
          {
            memo: campaign,
          }
        )
      )

      if (txProfile.data) {
        break
      }
    }

    queueMicrotask(async () => {
      while (true) {
        const ph_project_api_key = process.env.NEXT_PUBLIC_APP_POSTHOG_KEY
        const ph_cookie_key = `ph_${ph_project_api_key}_posthog`

        const getCookie = (name: string) => {
          return document.cookie
            .split('; ')
            .find((row) => row.startsWith(`${name}=`))
            ?.split('=')[1]
        }

        const nbzKey = JSON.parse(getCookie('nbz_click') || '{}')
        const posthog = JSON.parse(localStorage.getItem(ph_cookie_key) || '{}')

        const response = await tryCatchAsync(
          useTRPCClient().private.account.createAccount.mutate({
            click_id: nbzKey.click_id || posthog.click_id,
            distinct_id: nbzKey.distinct_id || posthog.$device_id,
            wallet_type: wallet,
          })
        )

        if (response.data) {
          break
        }
      }
    })

    queryClient.setQueryData(['USER_PROFILE_ME', address], () => {
      return {
        address,
        profile: {
          ...profile,
          user_name: getAnonymousUserName(profile.user_name),
        },
        referrer: referral,
      }
    })
    queryClient.cancelQueries({
      queryKey: ['USER_PROFILE_ME', address],
    })

    // Update Apollo GraphQL cache
    await apolloClient.cache.updateQuery(
      {
        query: GET_USER_PROFILE,
        variables: {
          address: address!,
        },
      },
      (data) => {
        return {
          nfts: data?.nfts || null,
          user: {
            ...data?.user,
            profileUserName: profile.user_name,
            profileAvatar: profile.avatar,
          },
        }
      }
    )

    navigateTo(null)
  }

  return (
    <SignInModalContext.Provider
      value={{
        navigateTo,
        handleWalletFlow,
        handleRegister,
        onClose: () => navigateTo(null),
        context: { user, wallet },
      }}
    >
      <Wrapper
        isOpen={!!step}
        onClose={() => navigateTo(null)}
        isExpiredStep={step?.screen === 'Expiry'}
        closeOnOverlayClick={step?.screen === 'SignIn'}
      >
        <Flex flexDir={'column'} p={7} pt={{ base: 7, md: 6, lg: 7 }} flex="1">
          <Flex flexDir={'column'} h={'full'} pos={'relative'}>
            {step?.component}
          </Flex>
        </Flex>
      </Wrapper>
    </SignInModalContext.Provider>
  )
})

const Wrapper = ({
  onClose,
  isOpen,
  children,
  closeOnOverlayClick = false,
  isExpiredStep = false,
}: {
  onClose: () => void
  isOpen: boolean
  children: React.ReactElement
  closeOnOverlayClick: boolean
  isExpiredStep: boolean
}) => {
  const [isDesktop] = useMediaQuery('(min-width: 768px)')

  if (!isDesktop) {
    return (
      <Drawer
        placement={'bottom'}
        onClose={onClose}
        isOpen={isOpen}
        closeOnOverlayClick={closeOnOverlayClick}
        closeOnEsc={closeOnOverlayClick}
        lockFocusAcrossFrames={true}
      >
        <DrawerOverlay bg={'blackAlpha.800'} />
        <DrawerContent bg={'ninja.600'}>
          <DrawerBody overflow={'visible'} p={0}>
            <Box pos={'relative'}>
              <MobileBanner />
              {children}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Modal
      isCentered={true}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEsc={closeOnOverlayClick}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay bg={'blackAlpha.800'} />
      <ModalContent
        maxW={isExpiredStep ? 'md' : { md: '432px', lg: '840px' }}
        roundedTop={{ md: '0', lg: '1.25rem' }}
        marginLeft={2.5}
        marginRight={2.5}
      >
        {!isExpiredStep && (
          <ModalCloseButton
            zIndex={10}
            top={{ base: '32px', md: '26px', lg: '32px' }}
            right={'28px'}
            onClick={onClose}
            size="sm"
          />
        )}
        <ModalBody p={0} pos={'relative'}>
          <Box display={{ base: 'none', md: 'block', lg: 'none' }}>
            <MobileBanner />
          </Box>
          <Flex gap={5}>
            {!isExpiredStep && (
              <Box display={{ base: 'none', lg: 'block' }}>
                <Banner isDisabled={false} />
              </Box>
            )}
            {children}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const SignInModalContext = createContext({
  handleWalletFlow: (_walletId: string, _ethAddress: string) => {},
  onClose: () => {},
  navigateTo: (
    screen: keyof typeof ScreenType,
    _?: (Attributes & Parameters<(typeof ScreenType)[typeof screen]['component']>[0]) | null
  ) => {},
  handleRegister: (() => {}) as any,
  context: {
    user: {} as User | undefined,
    wallet: '' as string | undefined,
  },
})

export const useSignInModal = () => use(SignInModalContext)

import SignInBg from '@/public/assets/images/sign-in/sign-in-bg-2.png'
import SignInBgDepositBonus from '@/public/assets/images/sign-in/sign-in-bg-deposit-bonus.png'
import SignInImage from '@/public/assets/images/sign-in/sign-in-image-2.png'

const Banner = ({ isDisabled }: { isDisabled: boolean }) => {
  const { enteringMode } = useEnteringModeValue()

  if ([EnteringMode.DepositBonus100, EnteringMode.Login].includes(enteringMode)) {
    return (
      <Box opacity={isDisabled ? 0.5 : 1} position={'relative'} width={'388px'} flexShrink={0}>
        <Box borderRadius={'20px'} overflow={'hidden'}>
          <Image src={SignInBgDepositBonus} alt={''} />
        </Box>
      </Box>
    )
  }

  return (
    <Box opacity={isDisabled ? 0.5 : 1} position={'relative'} width={'388px'} flexShrink={0}>
      <Box borderRadius={'20px'} overflow={'hidden'}>
        <Image src={SignInBg} alt={''} />
      </Box>
      <Image pos={'absolute'} bottom={0} maxW={'none'} right={'-70px'} src={SignInImage} alt={''} />
    </Box>
  )
}

import MobileBannerImage from '@/public/assets/images/sign-in/sign-in-image-mobile.png'
import MobileBannerBg from '@/public/assets/images/sign-in/sign-in-bg-mobile.png'
import MobileBannerImageDepositBonus from '@/public/assets/images/sign-in/sign-in-image-mobile-deposit-bonus.png'
import MobileBannerBgDepositBonus from '@/public/assets/images/sign-in/sign-in-bg-mobile-deposit-bonus.png'
import { getAnonymousUserName } from '@/helpers/getAnonymousUserName'

const MobileBanner = () => {
  const { enteringMode } = useEnteringModeValue()

  if ([EnteringMode.DepositBonus100, EnteringMode.Login].includes(enteringMode)) {
    return (
      <Flex pos={'absolute'} bottom={'100%'} left={0} right={0}>
        <Box pos={'relative'} w={'full'}>
          <Image w={'full'} h={'60px'} roundedTop={'20px'} src={MobileBannerBgDepositBonus} alt={''} />
          <Flex fontWeight={700} fontSize={'xl'} alignItems={'end'} pos={'absolute'} bottom={0} left={0}>
            <Image w={'200px'} src={MobileBannerImageDepositBonus} alt={''} />
            <Text ml={-4} mb={2} lineHeight={1.1}>
              You have <br />
              <Text
                display={'inline-block'}
                as={'span'}
                bg={'#7A4EF8'}
                bgBlendMode={'overlay'}
                bgGradient={`linear-gradient(90deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 25%, rgba(255, 255, 255, 0.5) 33%, rgba(255, 255, 255, 0.25) 66%, rgba(255, 255, 255, 0.25) 75%, rgba(255, 255, 255, 0.25) 100%)`}
                backgroundClip={'text'}
                __css={{
                  '-webkit-text-fill-color': 'transparent',
                }}
              >
                Deposit Bonus
              </Text>
            </Text>
          </Flex>
        </Box>
      </Flex>
    )
  }

  return (
    <>
      <Box pos={'absolute'} bottom={'100%'} left={0} right={0}>
        <Image w={'full'} h={'60px'} roundedTop={'20px'} src={MobileBannerBg} alt={''} />
      </Box>
      <Image pos={'absolute'} bottom={'100%'} left={0} src={MobileBannerImage} alt={''} />
    </>
  )
}
